import React, { Dispatch, SetStateAction } from 'react';
import {
  Button,
  Col,
  Form,
  Image,
  Input,
  Row,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { UploadFile } from 'antd/es/upload/interface';
// Types
import {
  ExtTalentAthleticLevel,
  ExtTalentStatus,
  StoreGender,
} from 'api/graphql-global-types';
import EnumSelect from 'components/common/EnumSelect/EnumSelect';
import { ExternalTalent } from 'helpers/externalTalents';
// Helpers
import {
  birthMonthOptions,
  birthYearOptions,
  isValidBirthDate,
} from 'helpers/externalTalents';

const { Title } = Typography;
const { Option } = Select;

type BasicInfoSectionProps = {
  avatarFileList: UploadFile[];
  birthDateOptions: number[];
  onBirthMonthChange: (value?: number) => void;
  onBirthYearChange: (value?: number) => void;
  selectedBirthMonth: number | null;
  selectedBirthYear: number | null;
  setAvatarFileList: Dispatch<SetStateAction<UploadFile[]>>;
  talent?: ExternalTalent | null;
};

const BasicInfoSection = ({
  avatarFileList,
  birthDateOptions,
  onBirthMonthChange,
  onBirthYearChange,
  selectedBirthMonth,
  selectedBirthYear,
  setAvatarFileList,
  talent,
}: BasicInfoSectionProps): JSX.Element => {
  return (
    <>
      <Title level={5}>Basic Info</Title>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="First name"
            name="firstName"
            rules={[
              {
                message: 'Please enter first name',
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="Middle name" name="middleName">
            <Input />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            label="Last name"
            name="lastName"
            rules={[
              {
                message: 'Please enter last name',
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="Athletic level" name="athleticLevel">
            <EnumSelect
              allowClear
              options={Object.values(ExtTalentAthleticLevel)}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="Gender" name="gender">
            <EnumSelect allowClear options={Object.values(StoreGender)} />
          </Form.Item>
        </Col>

        {talent && (
          <Col span={8}>
            <Form.Item label="Status" name="status">
              <EnumSelect options={Object.values(ExtTalentStatus)} />
            </Form.Item>
          </Col>
        )}
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Birth year"
            name="birthYear"
            rules={[
              {
                validator(_, value) {
                  if (!value || value >= 1900) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error('Birth year cannot be lower than 1900')
                  );
                },
              },
            ]}
          >
            <Select allowClear onChange={onBirthYearChange} showSearch>
              {birthYearOptions.map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            dependencies={['birthYear']}
            label="Birth month"
            name="birthMonth"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (!getFieldValue('birthYear')) {
                    return Promise.reject(
                      new Error('Select birth year before birth month')
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Select
              allowClear
              disabled={!selectedBirthYear}
              onChange={onBirthMonthChange}
            >
              {birthMonthOptions.map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            dependencies={['birthYear', 'birthMonth']}
            label="Birth day"
            name="birthDate"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.resolve();
                  }

                  const year = getFieldValue('birthYear');
                  const month = getFieldValue('birthMonth');

                  if (!year) {
                    return Promise.reject(
                      new Error('Select birth year before birth day')
                    );
                  }

                  if (!month) {
                    return Promise.reject(
                      new Error('Select birth month before birth day')
                    );
                  }

                  if (!isValidBirthDate(year, month, value)) {
                    return Promise.reject(
                      new Error('Please enter a realistic birth date')
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Select allowClear disabled={!selectedBirthMonth}>
              {birthDateOptions.map((value) => (
                <Option key={value} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Avatar">
        <Space align="start">
          {talent?.avatarUrl && !avatarFileList.length && (
            <Image
              height={72}
              preview={false}
              src={talent.avatarUrl}
              style={{ borderRadius: 8, objectFit: 'cover' }}
              width={72}
            />
          )}

          <Upload
            accept=".png,.jpg,.jpeg,.webp"
            beforeUpload={() => false}
            fileList={avatarFileList}
            maxCount={1}
            onChange={({ fileList }) => setAvatarFileList(fileList.slice(-1))}
            onRemove={() => {
              setAvatarFileList([]);
              return true;
            }}
          >
            <Button icon={<UploadOutlined />}>Upload avatar</Button>
          </Upload>
        </Space>
      </Form.Item>
    </>
  );
};

export default BasicInfoSection;
